import React, { useState, useEffect } from "react";
import Dashboard from "./components/HomePage/Dashboard";
import LeftSidebar from "./components/SideBar/LeftSidebar";
import ShopDashboard from "./components/Shop/ShopDashboard";
import ShopLogs from "./components/Shop/ShopLogs";
import TopBar from "./components/TopBar/TopBar";
import LoginPage from "./components/Auth/LoginPage";
import { auth } from "./components/Auth/Auth";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { useAlertData } from "./services/API/GetAlerts";
import { X } from "lucide-react";
// import KeyDashboard from "./components/KeysDashboard/KeyDashboard";
import Ecommerce from "./components/Ecommerce/Ecommerce";

const App = () => {
  const [activeView, setActiveView] = useState("Dashboard");
  // Stores the shop selected from the Shop List.
  const [selectedShopForLogs, setSelectedShopForLogs] = useState(null);
  const [user, setUser] = useState(null);

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useAlertData();

  useEffect(() => {
    let logoutTimer;

    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      // Clear any previously scheduled logout.
      clearTimeout(logoutTimer);

      if (!currentUser) {
        setUser(null);
        return;
      }

      try {
        // Get the original authentication time from Firebase.
        const tokenResult = await currentUser.getIdTokenResult();
        const loginTime = new Date(tokenResult.authTime).getTime();

        // Calculate when the 24-hour session expires.
        const expiresAt = loginTime + 24 * 60 * 60 * 1000;

        const remainingTime = expiresAt - Date.now();

        if (remainingTime <= 0) {
          // Sign out immediately if the session has expired.
          await signOut(auth);
          return;
        }

        setUser(currentUser);

        // Automatically return to the login page when the session expires.
        logoutTimer = setTimeout(async () => {
          // Hide the Dashboard immediately.
          setUser(null);

          try {
            // End the Firebase authentication session.
            await signOut(auth);
          } catch (error) {
            console.error("Automatic logout failed:", error);
          }
        }, remainingTime);
      } catch (error) {
        console.error("Failed to verify session expiration:", error);
        await signOut(auth);
      }
    });

    return () => {
      clearTimeout(logoutTimer);
      unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (isMobileMenuOpen) {
      setIsMobileMenuOpen(false);
    }
  }, [activeView]);

  const handleOpenShopLogs = (shop) => {
    // Store the selected shop before opening the Shop Logs page.
    setSelectedShopForLogs(shop);

    setActiveView("ShopLogs");
  };

  const handleViewChange = (view) => {
    // Clear the selected shop when leaving the Shop Logs page.
    if (view !== "ShopLogs") {
      setSelectedShopForLogs(null);
    }

    setActiveView(view);
  };

  const renderView = () => {
    switch (activeView) {
      case "Dashboard":
        return <Dashboard user={user} />;

      case "Shop":
        return <ShopDashboard onOpenShopLogs={handleOpenShopLogs} />;

      case "ShopLogs":
        return <ShopLogs initialShop={selectedShopForLogs} />;

      case "Ecommerce":
        return <Ecommerce />;

      default:
        return <Dashboard user={user} />;
    }
  };

  if (!user) {
    return <LoginPage />;
  }

  return (
    <div className="bg-stone-100 h-screen">
      <div className="grid h-full grid-cols-1 lg:grid-cols-[220px_1fr]">
        <div className="hidden lg:block h-full p-4">
          <LeftSidebar
            user={user}
            activeView={activeView}
            setActiveView={handleViewChange}
          />
        </div>

        <div
          className={`fixed inset-0 z-40 bg-black/30 transition-opacity lg:hidden ${
            isMobileMenuOpen ? "opacity-100" : "opacity-0 pointer-events-none"
          }`}
          onClick={() => setIsMobileMenuOpen(false)}
        ></div>

        <div
          className={`fixed top-0 left-0 h-full w-[220px] bg-white shadow-xl z-50 transition-transform lg:hidden ${
            isMobileMenuOpen ? "translate-x-0" : "-translate-x-full"
          }`}
        >
          <div className="p-4 h-full">
            <button
              onClick={() => {
                console.log("click");
                setIsMobileMenuOpen(false);
              }}
              className="absolute top-2 right-2 z-50 w-10 h-10 flex items-center justify-center text-[#222731] hover:bg-stone-200 rounded-md"
            >
              <X className="w-5 h-5" />
            </button>

            <LeftSidebar
              activeView={activeView}
              setActiveView={handleViewChange}
            />
          </div>
        </div>

        <div className="flex flex-col overflow-hidden h-full p-4">
          <TopBar onMenuClick={() => setIsMobileMenuOpen(true)} />

          <main className="flex-1 overflow-y-auto no-scrollbar">
            {renderView()}
          </main>
        </div>
      </div>
    </div>
  );
};

export default App;
